import React from "react";
import { useToast } from "../../../provider/ToastProvider";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings } from "../../../utils/confighelpers";
import { FcModal, FieldError, FieldLabel, inputClass } from "../schedule/ui";
import { apiResult } from "./memberUtils";

/**
 * "Add to members": trial guest (booked a free trial class) -> member. POST bookings/makeClubMember { consumerId, email }:
 * creates their payment profile (Stripe customer) at your club, makes them a member and emails them
 * "Thanks for trying a class … Join <club> as a member" (choose a password, then buy credits) — no free credit.
 * The email from the trial form isn't verified, so the club checks it here first.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function MakeMemberModal({ open, guest, onClose, onDone, reason = "" }) {
  const { addSuccessToast, addErrorToast } = useToast();
  const [email, setEmail] = React.useState("");
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const first = (guest?.fullName || "").split(" ")[0] || "this guest";

  React.useEffect(() => {
    if (open) {
      setEmail(guest?.email || "");
      setError("");
    }
  }, [open, guest]);

  const submit = async () => {
    const v = email.trim().toLowerCase();
    if (!EMAIL_RE.test(v)) {
      setError("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const res = await axiosInstance({ method: "post", url: `${getUrlForBookings()}makeClubMember`, data: { consumerId: guest?.consumerId || guest?._id, email: v } });
      const [ok, , msg] = apiResult(res);
      if (ok) {
        addSuccessToast(msg || `${guest?.fullName || "The guest"} is added to your members.`);
        onDone && onDone();
        onClose();
      } else {
        addErrorToast(msg || "Couldn't add this guest to your members.");
      }
    } catch (e) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="TRIAL GUEST"
      title={`Add ${first} to your members`}
      subtitle={reason || `${first} tried a class with you. Add them to your members when they want to join.`}
      primaryLabel="Add to members"
      busy={busy}
      onPrimary={submit}
    >
      <ul className="flex flex-col gap-2 text-sm text-gray-700">
        {[
          "They become a member of your club, so you can add passes and subscriptions for them.",
          "We email them: thanks for trying a class, with a button to join as a member.",
          "They choose a password and can buy credits straight away. No free credit is added.",
        ].map((t, i) => (
          <li key={i} className="flex gap-2.5">
            <span className="mt-[2px] flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F3E9FF] text-[11px] font-black text-indigo-bz">{i + 1}</span>
            {t}
          </li>
        ))}
      </ul>
      <div>
        <FieldLabel htmlFor="make-member-email">Their email</FieldLabel>
        <input
          id="make-member-email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError("");
          }}
          className={inputClass(!!error)}
          placeholder="name@example.com"
        />
        <FieldError>{error}</FieldError>
        {!error && <p className="mt-1.5 text-xs text-gray-500">They typed this on the trial form; it isn't checked yet. Correct it if it's wrong, the join link goes here.</p>}
      </div>
    </FcModal>
  );
}
